import React, { useEffect, useState, useCallback } from "react";
import FileUploadSource from "../../components/uploads/FileUploadSource";
import { LifecycleAlert } from "../../lib/lifecycleApi.jsx";
import { OriginSummary } from "../../lib/origin.jsx";
import { getOverview } from "../../lib/uploadsApi";
import { Tile, Badge, SectionHeader, StatusDot } from "../../components/Primitives";
import S3ConnectionModal from "../../components/S3ConnectionModal";
import SchemaDiscoveryDrawer from "../../components/SchemaDiscoveryDrawer";
import PilotPreflightChecklist from "../../components/PilotPreflightChecklist";
import CredentialRotationModal from "../../components/CredentialRotationModal";
import IngestWatchFeed from "../../components/IngestWatchFeed";
import { cn } from "../../lib/utils";
import { useRole } from "../../lib/role.jsx";
import {
  listConnections, testConnection, deleteConnection, listAudit, s3Health,
  downloadAttestationPdf, kickAutoDiscover,
} from "../../lib/s3Api";
import {
  Database, Activity, Wifi, CheckCircle2, AlertTriangle, RefreshCw, Plus,
  Trash2, Sparkles, ShieldCheck, History, KeyRound, Copy, Download, FileText,
} from "lucide-react";

// Amazon S3 brand-orange inline icon
function S3Icon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2 4 6v12l8 4 8-4V6l-8-4Zm0 2.2 5.9 2.95L12 10.1 6.1 7.15 12 4.2Zm-6 4.5 5 2.5v7.75l-5-2.5V8.7Zm12 0v7.75l-5 2.5V11.2l5-2.5Z"/>
    </svg>
  );
}

const STATUS_LABEL = {
  draft:     { badge: "info",     label: "Draft" },
  connected: { badge: "success",  label: "Connected" },
  failing:   { badge: "critical", label: "Failing" },
  degraded:  { badge: "warning",  label: "Degraded" },
  archived:  { badge: "neutral",  label: "Archived" },
};

export default function LiveDataPipeline() {
  const { user } = useRole();
  const [testStatus, setTestStatus] = useState({});
  const [s3Connections, setS3Connections] = useState([]);
  const [loadingConns, setLoadingConns] = useState(true);
  const [selectedS3, setSelectedS3] = useState(null);
  const [s3Modal, setS3Modal] = useState(false);
  const [discoveryConn, setDiscoveryConn] = useState(null);
  const [toast, setToast] = useState(null);
  const [audit, setAudit] = useState([]);
  const [health, setHealth] = useState(null);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [waiting, setWaiting] = useState(null);

  const refreshHealth = useCallback(async () => {
    try {
      const h = await s3Health();
      setHealth(h);
    } catch (e) {
      setHealth({ ok: false, mechanism: "unknown", error: e.message });
    }
  }, []);

  const refreshConnections = useCallback(async () => {
    setLoadingConns(true);
    try {
      const res = await listConnections();
      setS3Connections(res.connections || []);
      if (res.connections?.length && !selectedS3) setSelectedS3(res.connections[0].id);
    } catch (e) {
      setToast(`Could not load S3 connections: ${e.message}`);
      setTimeout(() => setToast(null), 3200);
    } finally {
      setLoadingConns(false);
    }
  }, [selectedS3]);

  const refreshAudit = useCallback(async () => {
    try {
      const res = await listAudit(null, 30);
      setAudit(res.entries || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refreshConnections(); refreshAudit(); refreshHealth(); }, [refreshConnections, refreshAudit, refreshHealth]);
  useEffect(() => { getOverview().then(o => setWaiting(o.bronze.waiting)).catch(() => {}); }, [audit]);

  const s3Conn = s3Connections.find(c => c.id === selectedS3) || s3Connections[0] || null;

  function pop(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  async function handleTestS3(id) {
    setTestStatus(s => ({ ...s, [id]: "testing" }));
    try {
      const res = await testConnection(id);
      const ok = !!(res.test?.ok && res.test?.list_ok);
      setTestStatus(s => ({ ...s, [id]: ok ? "success" : "fail" }));
      setS3Connections(prev => prev.map(c => c.id === id ? (res.connection || c) : c));
      pop(ok
        ? `AssumeRole + list OK · ${res.test?.sample_key_count ?? 0} keys under approved prefix.`
        : `Connection failed: ${res.test?.error || "unknown AWS error"}`);
      refreshAudit();
    } catch (e) {
      setTestStatus(s => ({ ...s, [id]: "fail" }));
      pop(`Test failed: ${e.message}`);
    }
  }

  async function handleDeleteS3(id) {
    if (!window.confirm("Delete this S3 connection? This does not touch AWS — only removes the record here.")) return;
    try {
      await deleteConnection(id);
      pop("S3 connection removed.");
      await refreshConnections();
      if (selectedS3 === id) setSelectedS3(null);
      refreshAudit();
    } catch (e) {
      pop(`Delete failed: ${e.message}`);
    }
  }

  function onSavedConnection(c) {
    setS3Connections(prev => {
      const idx = prev.findIndex(x => x.id === c.id);
      if (idx >= 0) { const next = prev.slice(); next[idx] = c; return next; }
      return [c, ...prev];
    });
    setSelectedS3(c.id);
    pop(`S3 connection "${c.name}" saved · status: ${c.status}.`);
    refreshAudit();
  }

  const s3Total = s3Connections.length;
  const s3Connected = s3Connections.filter(c => c.status === "connected").length;

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="Admin Console" title="Live Data Pipeline"
        description="Bring data in: upload files or stage S3 files from Discover. Everything lands in Bronze for review in the Data Management Console. S3 access uses STS AssumeRole with a customer-specific External ID." />

      <OriginSummary variant="system" />
      <LifecycleAlert isItHead={user?.role === "it_head"} />
      <FileUploadSource />

      {/* Backend workload identity */}
      <div data-testid="s3-workload-identity"
           className={cn("p-3 border rounded-sm flex items-start gap-3 flex-wrap",
             health?.ok ? "border-success/40 bg-success/5"
             : health ? "border-warning/40 bg-warning/5"
             : "border-border bg-surface-2/40")}>
        <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center shrink-0",
          health?.ok ? "bg-success/15 text-success" : "bg-warning/15 text-warning")}>
          <KeyRound size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted">Backend workload identity</span>
            <Badge status={health?.ok ? "success" : health ? "warning" : "info"}>
              {health?.ok ? "Verified via sts:GetCallerIdentity" : health ? "Not configured" : "Checking…"}
            </Badge>
            {health?.mechanism && <Badge status="info">mechanism: {health.mechanism}</Badge>}
          </div>
          <div className="text-[11.5px] mt-1 leading-relaxed">
            <span className="text-muted">Published role ARN (paste into 1Rotary's trust policy):</span>{" "}
            <code className="font-mono-tabular text-primary break-all">{health?.published_role_arn || "…"}</code>
          </div>
          {health?.ok && (
            <div className="text-[11px] mt-1 font-mono-tabular text-fg/90">
              caller: {health.caller_arn} · account {health.account} · region {health.region}
            </div>
          )}
          {!health?.ok && health?.error && (
            <div className="text-[11px] mt-1 text-warning leading-snug break-words" data-testid="s3-workload-error">
              {health.error}
            </div>
          )}
        </div>
        <button data-testid="s3-health-refresh" onClick={refreshHealth}
                className="w-8 h-8 shrink-0 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm">
          <RefreshCw size={11} />
        </button>
      </div>

      <div className="tile grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
        <Agg label="S3 Connections" value={s3Total} status="info" />
        <Agg label="S3 Connected" value={`${s3Connected}/${s3Total}`} status={s3Connected === s3Total && s3Total > 0 ? "success" : "warning"} />
        <Agg label="Approved Pilot Prefix" value="1rotary/pilot/" mono status="info" />
        <Agg label="Waiting in Bronze" value={waiting ?? "…"} status={waiting ? "warning" : "success"} testid="ldp-bronze-waiting" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* 1Rotary pilot preflight checklist — 3 live-tracked steps */}
        <PilotPreflightChecklist
          connection={s3Connections.find(c => c.bucket === "1rotary-ect-odt-pilot") || null}
          backendHealth={health}
          onTest={handleTestS3}
          onReload={refreshConnections}
          onPop={pop}
          onRotate={user?.role === "it_head" ? () => setRotateOpen(true) : null}
        />

        {/* Credential Rotation Modal (IT Head only) */}
        <CredentialRotationModal
          open={rotateOpen}
          onClose={() => setRotateOpen(false)}
          connection={s3Connections.find(c => c.bucket === "1rotary-ect-odt-pilot") || null}
          onDone={() => { refreshConnections(); refreshAudit(); refreshHealth(); }}
        />

        {/* 1Rotary Ingest Watch — live feed of new / updated files */}
        <IngestWatchFeed
          connectionId={(s3Connections.find(c => c.bucket === "1rotary-ect-odt-pilot") || {}).id}
        />

        {/* Amazon S3 Connections — REAL */}
        <Tile className="col-span-12" testid="s3-connections"
              title="Amazon S3 · Cross-Account Data Pipelines"
              action={
                <div className="flex items-center gap-2">
                  <Badge status="success"><ShieldCheck size={10} className="inline mr-1" />STS AssumeRole + External ID</Badge>
                  <Badge status={s3Connected === s3Total && s3Total > 0 ? "success" : "warning"}>{s3Connected}/{s3Total} connected</Badge>
                  <button data-testid="s3-add-btn" onClick={() => setS3Modal(true)}
                          className="px-3 py-1.5 text-[11px] font-semibold bg-primary text-primary-fg hover:opacity-90 rounded-sm inline-flex items-center gap-1">
                    <Plus size={11} /> Connect a Bucket
                  </button>
                </div>
              }>
          <div className="grid grid-cols-12 gap-4">
            {/* Cards */}
            <div className="col-span-12 lg:col-span-7 space-y-2">
              {loadingConns && (
                <div className="p-6 border border-dashed border-border rounded-sm text-center text-muted text-[12px] inline-flex items-center gap-2 justify-center w-full">
                  <RefreshCw size={12} className="animate-spin" /> Loading connections…
                </div>
              )}
              {!loadingConns && s3Connections.length === 0 && (
                <div className="p-6 border border-dashed border-border rounded-sm text-center text-muted text-[12px]" data-testid="s3-empty">
                  No S3 connections yet. Click <span className="text-primary font-medium">Connect a Bucket</span> to publish the onboarding
                  guide, generate copy-ready IAM policies, and register a real bucket.
                </div>
              )}
              {s3Connections.map(c => {
                const isSel = selectedS3 === c.id;
                const test = testStatus[c.id];
                const meta = STATUS_LABEL[c.status] || STATUS_LABEL.draft;
                return (
                  <div key={c.id} onClick={() => setSelectedS3(c.id)} data-testid={`s3-conn-${c.id}`}
                       className={cn("p-3 border rounded-sm cursor-pointer transition-colors",
                         isSel ? "border-primary bg-primary/5" : "border-border bg-surface-2/40 hover:bg-surface-2")}>
                    <div className="flex items-start gap-3">
                      <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center shrink-0",
                        c.status === "connected" ? "bg-success/15 text-success"
                        : c.status === "failing" ? "bg-critical/15 text-critical"
                        : c.status === "degraded" ? "bg-warning/15 text-warning"
                        : "bg-primary/15 text-primary")}>
                        <S3Icon />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{c.name}</span>
                          <Badge status={meta.badge}>{meta.label}</Badge>
                          {c.layout_hint && <Badge status="info">{c.layout_hint}</Badge>}
                        </div>
                        <div className="text-[10px] text-muted font-mono-tabular mt-0.5 truncate">s3://{c.bucket}/{c.approved_prefix}</div>
                        <div className="flex items-center gap-3 text-[10px] text-muted mt-1 flex-wrap font-mono-tabular">
                          <span>{c.region}</span>
                          <span>· cross-account role</span>
                          <span>· {c.encryption}</span>
                          {c.requester_pays && <span className="text-warning">· requester pays</span>}
                        </div>
                        {c.last_error && (
                          <div className="text-[10px] text-critical mt-1 leading-snug break-words" data-testid={`s3-error-${c.id}`}>{c.last_error}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`s3-discover-${c.id}`} onClick={(e) => { e.stopPropagation(); setDiscoveryConn(c); }}
                                disabled={c.status !== "connected"}
                                title={c.status !== "connected" ? "Test the connection first" : "Discover schemas"}
                                className={cn("px-3 py-1 text-[10px] border rounded-sm flex items-center gap-1 font-semibold transition-colors",
                                  c.status === "connected"
                                    ? "border-primary/50 text-primary hover:bg-primary hover:text-primary-fg"
                                    : "border-border text-muted cursor-not-allowed")}>
                          <Sparkles size={10} /> Discover
                        </button>
                        <button data-testid={`s3-test-${c.id}`} onClick={(e) => { e.stopPropagation(); handleTestS3(c.id); }}
                          className={cn("px-3 py-1 text-[10px] border rounded-sm flex items-center gap-1",
                            test === "success" ? "border-success text-success bg-success/10" :
                            test === "testing" ? "border-info text-info" :
                            test === "fail" ? "border-critical text-critical bg-critical/5" :
                            "border-border hover:bg-surface")}>
                          {test === "testing" ? <RefreshCw size={10} className="animate-spin" /> :
                           test === "success" ? <CheckCircle2 size={10} /> :
                           test === "fail"    ? <AlertTriangle size={10} /> : <Wifi size={10} />}
                          {test === "testing" ? "Testing..." : test === "success" ? "OK" : test === "fail" ? "Failed" : "Test"}
                        </button>
                        <button data-testid={`s3-remove-${c.id}`} onClick={(e) => { e.stopPropagation(); handleDeleteS3(c.id); }}
                                className="w-7 h-7 flex items-center justify-center border border-border hover:border-critical hover:text-critical rounded-sm">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Drilldown */}
            {s3Conn && (
              <Tile className="col-span-12 lg:col-span-5 !bg-transparent !border-0 !p-0" testid="s3-conn-details" title={null}>
                <div className="p-3 border border-border rounded-sm bg-surface-2/40 space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">Connection Details</div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div><div className="text-muted">Bucket</div><div className="font-mono-tabular text-primary truncate">{s3Conn.bucket}</div></div>
                      <div><div className="text-muted">Region</div><div className="font-mono-tabular">{s3Conn.region}</div></div>
                      <div className="col-span-2"><div className="text-muted">Approved prefix</div><div className="font-mono-tabular text-primary break-all">{s3Conn.approved_prefix}</div></div>
                      <div className="col-span-2"><div className="text-muted">Role ARN</div><div className="font-mono-tabular text-[10px] break-all">{s3Conn.role_arn}</div></div>
                      <div><div className="text-muted">Encryption</div><div>{s3Conn.encryption}</div></div>
                      <div><div className="text-muted">Requester Pays</div><div>{s3Conn.requester_pays ? "Yes" : "No"}</div></div>
                    </div>
                  </div>
                  <hr className="border-border" />
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div><div className="text-muted">Status</div>
                      <div><Badge status={(STATUS_LABEL[s3Conn.status] || STATUS_LABEL.draft).badge}>{(STATUS_LABEL[s3Conn.status] || STATUS_LABEL.draft).label}</Badge></div>
                    </div>
                    <div><div className="text-muted">Last test</div><div className="font-mono-tabular">{s3Conn.last_test_ts ? s3Conn.last_test_ts.replace("T", " ").slice(0, 16) : "—"}</div></div>
                    <div><div className="text-muted">Layout hint</div><div className="truncate">{s3Conn.layout_hint || "—"}</div></div>
                    <div><div className="text-muted">_meta sidecar</div><div>{s3Conn.layout_meta_probe ? "Detected" : "Not found"}</div></div>
                  </div>
                  {s3Conn.last_error && (
                    <>
                      <hr className="border-border" />
                      <div className="text-[11px] text-critical leading-snug" data-testid="s3-details-error">
                        <span className="text-critical uppercase tracking-widest text-[9px] block mb-1 font-semibold">Last error</span>
                        {s3Conn.last_error}
                      </div>
                    </>
                  )}
                  {s3Conn.note && (
                    <>
                      <hr className="border-border" />
                      <div className="text-[11px] text-fg/80 leading-relaxed">
                        <span className="text-muted uppercase tracking-widest text-[9px] block mb-1 font-semibold">Notes</span>
                        {s3Conn.note}
                      </div>
                    </>
                  )}
                </div>
              </Tile>
            )}
          </div>
        </Tile>

        {/* Audit log — live from Mongo */}
        <Tile className="col-span-12" testid="s3-audit"
              title="S3 audit log · recent"
              action={
                <div className="flex items-center gap-2">
                  <Badge status="info"><History size={10} className="inline mr-1" /> {audit.length} entries</Badge>
                  <button data-testid="s3-audit-refresh" onClick={refreshAudit}
                          className="w-7 h-7 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm">
                    <RefreshCw size={11} />
                  </button>
                </div>
              }>
          {audit.length === 0 ? (
            <div className="text-muted text-[12px]">No S3 activity yet. Test a connection or open Discovery to generate entries.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-muted">
                    <th className="py-1.5 px-2 text-left border-b border-border">When</th>
                    <th className="py-1.5 px-2 text-left border-b border-border">Op</th>
                    <th className="py-1.5 px-2 text-left border-b border-border">Outcome</th>
                    <th className="py-1.5 px-2 text-left border-b border-border">Connection</th>
                    <th className="py-1.5 px-2 text-left border-b border-border">Detail</th>
                  </tr>
                </thead>
                <tbody className="font-mono-tabular">
                  {audit.map((e, i) => (
                    <tr key={i} className="border-b border-border last:border-b-0">
                      <td className="py-1 px-2 text-muted">{(e.ts || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="py-1 px-2">{e.op}</td>
                      <td className="py-1 px-2">
                        <Badge status={e.outcome === "ok" ? "success" : e.outcome === "failed" ? "critical" : "info"}>{e.outcome}</Badge>
                      </td>
                      <td className="py-1 px-2 truncate max-w-[180px]">{e.connection_id || "—"}</td>
                      <td className="py-1 px-2 text-muted text-[10.5px] break-words max-w-[420px]">{e.detail || e.key || e.prefix || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Tile>

      </div>

      <S3ConnectionModal open={s3Modal} onClose={() => { setS3Modal(false); refreshConnections(); refreshAudit(); }} onSaved={onSavedConnection} />
      <SchemaDiscoveryDrawer open={!!discoveryConn} connection={discoveryConn}
                             onClose={() => { setDiscoveryConn(null); refreshAudit(); }} />

      {toast && (
        <div data-testid="s3-toast" className="fixed bottom-6 right-6 z-50 bg-surface border border-border-strong rounded-sm shadow-lg px-4 py-3 flex items-center gap-2 text-sm">
          <Activity size={14} className="text-primary" /> {toast}
        </div>
      )}
    </div>
  );
}

function Agg({ label, value, status, mono, testid }) {
  return (
    <div className="px-4 py-3" data-testid={testid}>
      <div className="kpi-label flex items-center gap-2">{status && <StatusDot status={status} />}{label}</div>
      <div className={cn("kpi-value text-2xl mt-1.5", mono && "font-mono-tabular text-base")}>{value}</div>
    </div>
  );
}
